import 'focus-visible'

import React, {
    FC, lazy, Suspense, useCallback, useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ModalLoading } from '~/components/modals/ModalLoading'
import { PageMeta } from '~/components/PageMeta'
import { SiteFooter } from '~/components/site-footer'
import { SiteHeader } from '~/components/site-header'
import { FaqStructuredData, SiteStructuredData } from '~/components/StructuredData'
import { PageKey } from '~/constants/pageMeta'

import { homepageFaqStructuredData, SectionAskedQuestion } from './components/section-asked-questions'
import { SectionBanner } from './components/section-banner'
import { SectionGetStarted } from './components/section-get-started'
import { SectionJoin } from './components/section-join'
import { SectionMap } from './components/section-map'
import { SectionPartners } from './components/section-partners'
import { SectionUserShipping } from './components/section-user-shipping'
import { SectionWorkflow } from './components/section-workflow'
import { closeFormRegistration, openFormRegistration } from './redux/rtk/mainSlice'
import { RootState } from './redux/store/get-store'

const RegistrationModalWrapper = lazy(() => import('~/components/modals/RegistrationModalWrapper')
    .then((module) => ({ default: module.RegistrationModalWrapper })))
const PdfDownloadModal = lazy(() => import('~/components/modals/pdf-download-modal/PdfDownloadModal')
    .then((module) => ({ default: module.PdfDownloadModal })))

const MainPage: FC = () => {
    const dispatch = useDispatch()
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false)
    const [hasOpenedRegistration, setHasOpenedRegistration] = useState(false)

    const user = useSelector((state: RootState) => state.user)
    const url = useSelector((state: RootState) => state.url)
    const isRegistrationFormOpen = useSelector((state: RootState) => state.isRegistrationFormOpen)

    const onOpenModalRegistration = useCallback(() => {
        setHasOpenedRegistration(true)
        dispatch(openFormRegistration())
    }, [dispatch])

    const onOpenPdfModal = useCallback(() => {
        setIsPdfModalOpen(true)
    }, [])

    const onClosePdfModal = useCallback(() => {
        setIsPdfModalOpen(false)
    }, [])

    const onCloseRegistration = useCallback(() => {
        dispatch(closeFormRegistration())
    }, [dispatch])

    return (
        <>
            <PageMeta pageKey={PageKey.MAIN} />
            <SiteStructuredData />
            <FaqStructuredData items={homepageFaqStructuredData} />
            <SiteHeader
                documentTitle="Air Cargo Charter Marketplace | World Trans Air"
                url={url}
                onOpenRegistration={onOpenModalRegistration}
            />
            <main>
                <SectionBanner openModal={onOpenModalRegistration} openPdfModal={onOpenPdfModal} url={url} />
                <SectionPartners />
                <SectionMap />
                <SectionWorkflow />
                <SectionUserShipping />
                {!user && (
                    <SectionJoin
                        openModal={onOpenModalRegistration}
                        url={url}
                    />
                )}
                <SectionAskedQuestion />
                <SectionGetStarted openModal={onOpenModalRegistration} />
            </main>
            <SiteFooter />
            {(hasOpenedRegistration || isRegistrationFormOpen) && (
                <Suspense fallback={isRegistrationFormOpen ? <ModalLoading onClose={onCloseRegistration} /> : null}>
                    <RegistrationModalWrapper />
                </Suspense>
            )}
            {isPdfModalOpen && (
                <Suspense fallback={<ModalLoading onClose={onClosePdfModal} />}>
                    <PdfDownloadModal isOpen onClose={onClosePdfModal} />
                </Suspense>
            )}
        </>
    )
}

export default MainPage
